import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CalendarDays, MapPin, Users, Award } from "lucide-react";

const bannerImage = "https://images.pexels.com/photos/9275222/pexels-photo-9275222.jpeg";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const formats = ["All", "In-Person", "Virtual", "Hybrid"];
  const filtered = filter === "All" ? events : events.filter((e) => e.format === filter);

  return (
    <div data-testid="events-page">
      <section className="relative">
        <div className="h-[320px] w-full overflow-hidden">
          <img src={bannerImage} alt="Conference audience" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[color:var(--azsam-ink)]/45" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 -mt-40 relative z-10">
          <div className="bg-white rounded-2xl p-10 shadow-xl border border-[color:var(--azsam-stone)] max-w-3xl">
            <div className="overline">Learn together</div>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl leading-tight">Events &amp; CME</h1>
            <p className="mt-4 text-[color:var(--azsam-ink-soft)] leading-relaxed">
              Case-based conferences, virtual workshops, and community gatherings that foster
              learning, collaboration, and professional growth, all tailored to the unique needs of
              addiction medicine professionals serving communities throughout Arizona.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex flex-wrap gap-2" data-testid="events-filters">
            {formats.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`events-filter-${f.toLowerCase()}`}
                className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors ${
                  filter === f
                    ? "bg-[color:var(--azsam-clay)] text-white border-[color:var(--azsam-clay)]"
                    : "bg-white text-[color:var(--azsam-ink)] border-[color:var(--azsam-stone)] hover:border-[color:var(--azsam-clay)]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6" data-testid="events-grid">
          {filtered.map((e) => (
            <Link to={`/events/${e.id}`} key={e.id} className="azsam-card overflow-hidden flex flex-col" data-testid={`event-${e.id}`}>
              <div className="h-56 overflow-hidden">
                <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-center gap-4 text-xs text-[color:var(--azsam-ink-soft)]">
                  <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} />{formatDate(e.date)}</span>
                  <span className="inline-flex items-center gap-1.5"><MapPin size={14} />{e.format}</span>
                </div>
                <h3 className="mt-3 font-heading text-2xl leading-snug">{e.title}</h3>
                <p className="mt-3 text-sm text-[color:var(--azsam-ink-soft)] leading-relaxed line-clamp-3">{e.description}</p>
                <div className="mt-6 pt-5 border-t border-[color:var(--azsam-stone)] flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3 text-[color:var(--azsam-ink-soft)]">
                    {e.cme_credits > 0 && <span className="inline-flex items-center gap-1"><Award size={13} />{e.cme_credits} CME</span>}
                    {(e.capacity > 0 || e.attendees > 0) && (
                      <span className="inline-flex items-center gap-1"><Users size={13} />{e.attendees}/{e.capacity}</span>
                    )}
                  </div>
                  <span className="text-[color:var(--azsam-clay)] font-semibold">View details →</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-[color:var(--azsam-ink-soft)]" data-testid="events-empty">
            No events match this filter — try a different format.
          </div>
        )}
      </section>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
